import { KeyboardEvent, memo, useEffect, useId, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import useStyles from './styles.Carousel'
import type { tProduct } from 'src/shared/frontendTypes/product'
import type { Images, imageSrcs } from 'src/shared/frontendTypes/images'
import getImageUrl from '../../functions/imageUrl'
import getImageAlt from '../../functions/imageAlt'
import config from 'src/config'
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'
import noImage from 'src/images/camera.svg'

// Keep zoom and the optional legacy inline-pinch viewer out of initial gallery work.
const PreviewModal = dynamic(() => import('./PreviewModal'), { ssr: false })
const LegacyImagesCarousel = dynamic(() => import('./LegacyImagesCarousel'))

interface Props {
  images: Images | imageSrcs
  product: tProduct
  mainImageHeight?: number
  mainImageSize?: 'xs' | 'sm' | 'md' | 'lg'
  disableImageZoomModal?: boolean
  disableTouchSwipe?: boolean
}

const NativeImagesCarousel: React.FC<Props> = ({
  images, product, mainImageHeight = 450, mainImageSize = 'md',
  disableImageZoomModal = false, disableTouchSwipe = false,
}) => {
  const classes = useStyles()
  const galleryId = useId()
  const gallery = useRef<HTMLDivElement>(null)
  const thumbnails = useRef<HTMLDivElement>(null)
  const [imageIndex, setImageIndex] = useState(0)
  const [showModal, setShowModal] = useState(false)
  const { id: productID, metaTitle } = product
  const firstImage = images[0]
  const firstImageKey = firstImage
    ? [productID, 'src' in firstImage ? firstImage.src : '', firstImage.xs,
      firstImage.sm, firstImage.md, firstImage.lg, firstImage.xl, firstImage.xxl].join('|')
    : String(productID)

  useEffect(() => {
    setImageIndex(0)
    // No size measurements or initial carousel setup: the browser paints SSR content.
    if (gallery.current?.scrollLeft) gallery.current.scrollTo({ left: 0, behavior: 'auto' })
    if (thumbnails.current?.scrollLeft) thumbnails.current.scrollTo({ left: 0, behavior: 'auto' })
  }, [firstImageKey])

  useEffect(() => {
    const strip = thumbnails.current
    if (!strip || (imageIndex === 0 && !strip.scrollLeft)) return
    const selected = strip.children[imageIndex] as HTMLElement | undefined
    if (!selected) return
    const left = selected.offsetLeft
    const right = left + selected.offsetWidth
    if (left < strip.scrollLeft) strip.scrollTo({ left, behavior: 'auto' })
    else if (right > strip.scrollLeft + strip.clientWidth) {
      strip.scrollTo({ left: right - strip.clientWidth, behavior: 'auto' })
    }
  }, [imageIndex])

  const selectImage = (index: number) => {
    const nextIndex = Math.max(0, Math.min(index, images.length - 1))
    const viewport = gallery.current
    if (!viewport) return
    setImageIndex(nextIndex)
    viewport.scrollTo({ left: nextIndex * viewport.clientWidth, behavior: 'auto' })
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return
    const nextIndex = event.key === 'ArrowRight' ? imageIndex + 1
      : event.key === 'ArrowLeft' ? imageIndex - 1
        : event.key === 'Home' ? 0 : event.key === 'End' ? images.length - 1 : undefined
    if (nextIndex === undefined) return
    event.preventDefault()
    selectImage(nextIndex)
  }

  if (images.length === 0) return <div className={classes.carouselWrapper} style={{ padding: 50 }}>
    <img alt='Kein Bild gefunden' src={noImage.src} height={450} width={450} loading='eager' />
  </div>

  return <div className={classes.carouselWrapper}>
    <div
      ref={gallery}
      id={galleryId}
      className={classes.nativeGallery}
      style={{ height: mainImageHeight, ...(disableTouchSwipe ? { overflowX: 'hidden' } : {}) }}
      role='region'
      aria-label='Produktbilder'
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onScroll={event => {
        const viewport = event.currentTarget
        if (viewport.clientWidth) setImageIndex(Math.max(0, Math.min(
          Math.round(viewport.scrollLeft / viewport.clientWidth), images.length - 1,
        )))
      }}
    >
      {images.map((image, index) => {
        const src = getImageUrl(image, productID, mainImageSize)
        const small = mainImageSize === 'md' ? getImageUrl(image, productID, 'sm') : null
        const picture = <>
          <picture>
            {small && <source media='(max-width: 382px) and (max-resolution: 1dppx)' srcSet={small} />}
            <img alt={getImageAlt(image, metaTitle)} src={src || noImage.src} height={450} width={450}
              loading={index === 0 ? 'eager' : 'lazy'}
              {...{ fetchpriority: index === 0 ? 'high' : 'auto' }} />
          </picture>
          <AiGeneratedBadge aiGenerated={image.aiGenerated} />
        </>
        return <div className={classes.nativeSlide} key={index} data-product-image={index}>
          {disableImageZoomModal ? picture : <button type='button' className={classes.imageButton}
            aria-label={`Bild ${index + 1} vergrößern`}
            onClick={() => {
              if (!getImageUrl(image, productID, 'lg')) return
              setImageIndex(index)
              setShowModal(true)
            }}>{picture}</button>}
        </div>
      })}
    </div>

    <div className={classes.nativeNavigation} aria-label='Bildauswahl'>
      {images.length > 1 && <button type='button' className={classes.galleryArrow}
        aria-label='Vorheriges Produktbild' disabled={imageIndex === 0}
        onClick={() => selectImage(imageIndex - 1)}>‹</button>}
      <div ref={thumbnails} className={classes.nativeThumbnails}>
        {images.map((image, index) => <button key={index} type='button'
          className={classes.nativeThumbnail} aria-label={`Bild ${index + 1} anzeigen`}
          aria-controls={galleryId} aria-pressed={imageIndex === index}
          onClick={() => selectImage(index)}>
          <img alt={getImageAlt(image, metaTitle)} src={getImageUrl(image, productID, 'xs') || noImage.src}
            width={100} height={100} loading='lazy' decoding='async' {...{ fetchpriority: 'low' }} />
          <AiGeneratedBadge aiGenerated={image.aiGenerated} size='compact' />
        </button>)}
      </div>
      {images.length > 1 && <button type='button' className={classes.galleryArrow}
        aria-label='Nächstes Produktbild' disabled={imageIndex === images.length - 1}
        onClick={() => selectImage(imageIndex + 1)}>›</button>}
    </div>

    {!disableImageZoomModal && showModal && <PreviewModal images={images} imageIndex={imageIndex}
      productID={productID} metaTitle={metaTitle} fullscreen={config.productImageViewer?.variant === 'fullscreen'}
      open handleClose={() => setShowModal(false)} />}
  </div>
}

const ImagesCarousel: React.FC<Props> = props => (
  // Preserve the existing configured inline pinch-zoom mode. Fullscreen viewers
  // and non-zoom cross-sell galleries need no Swiper until an image is opened.
  config.productImageViewer?.variant !== 'fullscreen' && !props.disableImageZoomModal
    ? <LegacyImagesCarousel {...props} />
    : <NativeImagesCarousel {...props} />
)

export default memo(ImagesCarousel)
